/** Error codes shared between server and client so the web layer can render
 * the right copy without parsing English error strings. */
export const ERROR_CODES = {
  AUTH_INVALID_CREDENTIALS: "auth/invalid-credentials",
  AUTH_TWO_FACTOR_REQUIRED: "auth/two-factor-required",
  AUTH_TOKEN_EXPIRED: "auth/token-expired",
  AUTH_TOKEN_INVALID: "auth/token-invalid",
  AUTH_TOKEN_REVOKED: "auth/token-revoked",
  AUTH_NO_DASHBOARD_ACCESS: "auth/no-dashboard-access",
  AUTH_TWO_FACTOR_ENROLLMENT_REQUIRED: "auth/two-factor-enrollment-required",
  AUTH_PASSWORD_CHANGE_REQUIRED: "auth/password-change-required",
  PERMISSION_DENIED: "permission/denied",
  UPSTREAM_UNREACHABLE: "upstream/unreachable",
  UPSTREAM_ERROR: "upstream/error",
  VALIDATION: "validation/failed",
  PAYLOAD_TOO_LARGE: "request/payload-too-large",
  NOT_FOUND: "not-found",
  RATE_LIMITED: "rate-limited",
  INTERNAL: "internal",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export class DashboardError extends Error {
  readonly code: ErrorCode;
  readonly statusCode: number;
  readonly details?: unknown;

  constructor(
    code: ErrorCode,
    message: string,
    statusCode = 500,
    details?: unknown,
  ) {
    super(message);
    this.name = "DashboardError";
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
  }

  toJSON(): { error: string; code: ErrorCode; details?: unknown } {
    return {
      error: this.message,
      code: this.code,
      ...(this.details !== undefined ? { details: this.details } : {}),
    };
  }
}

export const Unauthorized = (msg = "Unauthorized", code: ErrorCode = ERROR_CODES.AUTH_TOKEN_INVALID) =>
  new DashboardError(code, msg, 401);

export const Forbidden = (msg = "Forbidden") =>
  new DashboardError(ERROR_CODES.PERMISSION_DENIED, msg, 403);

/** 403 raised when an authenticated staff session has not yet enrolled a
 * second factor. Distinct code from PERMISSION_DENIED so the web client
 * can route the user into the 2FA setup flow instead of showing a dead
 * "forbidden" page. Mirrors portal-api's TWO_FACTOR_ENROLLMENT_REQUIRED. */
export const TwoFactorEnrollmentRequired = (
  msg = "Two-factor authentication enrollment is required.",
) => new DashboardError(ERROR_CODES.AUTH_TWO_FACTOR_ENROLLMENT_REQUIRED, msg, 403);

/** 403 raised when an authenticated session signed in with a one-time
 * invite password and must set its own before doing anything else.
 * Distinct code so the web client routes into the set-password flow
 * (checked BEFORE the 2FA-enrollment gate). */
export const PasswordChangeRequired = (
  msg = "You must set a new password before continuing.",
) => new DashboardError(ERROR_CODES.AUTH_PASSWORD_CHANGE_REQUIRED, msg, 403);

export const BadRequest = (msg: string, details?: unknown) =>
  new DashboardError(ERROR_CODES.VALIDATION, msg, 400, details);

export const NotFound = (msg = "Not found") =>
  new DashboardError(ERROR_CODES.NOT_FOUND, msg, 404);

export const Upstream = (msg: string, details?: unknown) =>
  new DashboardError(ERROR_CODES.UPSTREAM_ERROR, msg, 502, details);

/**
 * 503 raised when an upstream we MUST consult is unreachable / erroring —
 * as opposed to actively rejecting us.
 *
 * The distinction matters most on `/api/auth/refresh`: portal-api being
 * briefly down is NOT "your session is over". Answering 401 there made the
 * web client destroy a perfectly valid (year-long) refresh chain and bounce
 * the user to /login. A 503 tells the client "transient — keep your tokens,
 * try again", which is what `lib/api.ts` now does. See docs/session-lifetime.md.
 */
export const UpstreamUnavailable = (
  msg = "Upstream service is temporarily unavailable",
  details?: unknown,
) => new DashboardError(ERROR_CODES.UPSTREAM_UNREACHABLE, msg, 503, details);
